import type { AxiosRequestConfig } from 'axios';
import type { Flight, FlightAttachment } from '../types';
import type { ExpenseCostSplit } from './finance';
import { tapJournal } from '../journal/journalSync';
import { api } from './http';
import { createUuid } from '../utils/createUuid';

export type FlightWritePayload = {
  pilot_id: number;
  flight_date: string;
  hobbs_end: number;
  hours: number;
  flight_type: string;
  landings: number;
  oil?: number | null;
  fuel?: number | null;
  fuel_location?: string | null;
  comment?: string | null;
  refuels?: {
    liters: number;
    fuel_type?: string | null;
    price_per_liter?: number | null;
    location?: string | null;
    cost_split?: ExpenseCostSplit;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
  }[];
};

const flightParams = (data: FlightWritePayload) => ({
  pilot_id: data.pilot_id,
  flight_date: data.flight_date,
  hobbs_end: data.hobbs_end,
  hours: data.hours,
  flight_type: data.flight_type,
  landings: data.landings,
  oil: data.oil,
  fuel: data.fuel,
  fuel_location: data.fuel_location,
  comment: data.comment || '',
});

export const getFlights = (config?: AxiosRequestConfig) =>
  api.get<Flight[]>('/flights', config);
export const getFlightsByPilot = (pilotId: number) =>
  api.get<Flight[]>(`/flights/pilot/${pilotId}`);
export const addFlight = (data: FlightWritePayload) =>
  tapJournal(
    api.post<Flight>(
      '/flights',
      {
        refuels: data.refuels || [],
      },
      { params: flightParams(data) }
    )
  );
export const deleteFlight = (id: number) =>
  tapJournal(api.delete(`/flights/${id}`));
export const updateFlight = (id: number, data: FlightWritePayload) =>
  tapJournal(
    api.put(`/flights/${id}`, {
      ...flightParams(data),
      refuels: data.refuels || [],
    })
  );
export const splitFlight = (flightId: number, shares: { pilot_id: number; hours: number }[]) =>
  tapJournal(api.post(`/flights/${flightId}/split`, { shares }));

const multipartConfig = {
  transformRequest: [
    (body: unknown, headers: { [key: string]: unknown }) => {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        delete headers['Content-Type'];
      }
      return body;
    },
  ],
};

export const uploadFlightAttachment = (
  flightId: number,
  file: File,
  group_id?: string
) => {
  const form = new FormData();
  form.append('file', file);
  if (group_id) form.append('group_id', group_id);
  form.append('kind', 'tach');
  return api.post<FlightAttachment>(`/flights/${flightId}/attachments`, form, multipartConfig);
};

export const uploadFlightFiles = async (flightId: number, files: File[]) => {
  if (files.length === 0) return 0;
  const groupId = createUuid();
  let okCount = 0;
  for (const file of files) {
    await uploadFlightAttachment(flightId, file, groupId);
    okCount += 1;
  }
  return okCount;
};

export const getFlightAttachmentFile = (flightId: number, attachmentId: number) =>
  api.get<Blob>(`/flights/${flightId}/attachments/${attachmentId}/file`, {
    responseType: 'blob',
  });

export const downloadFlightAttachment = async (
  flightId: number,
  attachmentId: number,
  filename: string
) => {
  const res = await getFlightAttachmentFile(flightId, attachmentId);
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const deleteFlightAttachment = (flightId: number, attachmentId: number) =>
  api.delete<{ deleted: boolean; id: number }>(
    `/flights/${flightId}/attachments/${attachmentId}`
  );
